import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { IMessageBoxConfig } from '../components/molecules/MessageBox/types'
import { CaseContext } from '../contexts/CaseContext/CaseContext'
import { ProfileContext } from '../contexts/ProfileContext'
import { UserContext } from '../contexts/UserContext/UserContext'
import TitleContext from '../layouts/TitleContext'
import { NotificationContext } from './Context/NotificationContext'
import { INotification } from './Context/NotificationContext/types'
import { getClient, getToken, getUid } from './helper'

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height
  }
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

interface IOptions {
  openInNewTab?: boolean
}
interface IUseFetchExportParams {
  headers?: HeadersInit
  options?: IOptions
}

interface DataLoadingErrorObject {
  data?: Blob | null
  error?: any
  loading: boolean
}

type IUseFetchExport = [
  DataLoadingErrorObject,
  React.Dispatch<React.SetStateAction<string | undefined>>
]

export const useFetchExport: (headers: IUseFetchExportParams) => IUseFetchExport = ({
  headers,
  options = {}
}) => {
  const [data, setData] = useState<Blob | null>(null)
  const [error, setError] = useState<any>(false)
  const [loading, setLoading] = useState(false)
  const [url, setUrl] = useState<string | undefined>(undefined)
  const isDownloading = useRef(false)

  useEffect(() => {
    const fetchData = async () => {
      if (isDownloading.current) return
      isDownloading.current = true
      setError(false)
      setLoading(true)
      if (url) {
        try {
          let filename: string = ''
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              ...headers,
              'access-token': getToken() as string,
              uid: getUid() as string,
              client: getClient() as string
            }
          })
          filename = response.headers.get('content-disposition')?.split('"')[1] || ''
          const blob = await response.blob()
          const downloadUrl = window.URL.createObjectURL(new Blob([blob]))
          const link = document.createElement('a')
          link.href = downloadUrl
          if (options.openInNewTab) {
            const fileBlob = new Blob([blob], {
              type: response.headers.get('content-type') || 'application/octet-stream'
            })
            const fileUrl = URL.createObjectURL(fileBlob)
            window.open(fileUrl, '_blank')
          } else {
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)
          }
          setData(blob)
        } catch (err) {
          setError(err)
        } finally {
          setUrl(undefined)
          setLoading(false)
          isDownloading.current = false
        }
      }
    }

    if (url) fetchData()
  }, [url, headers, options.openInNewTab])

  return [{ data, loading, error }, setUrl]
}

export const useFetchAWS: (headers: IUseFetchExportParams) => IUseFetchExport = ({ headers }) => {
  const [data, setData] = useState<Blob | null>(null)
  const [error, setError] = useState<any>(false)
  const [loading, setLoading] = useState(false)
  const [url, setUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    const fetchData = async () => {
      setError(false)
      setLoading(true)
      if (url) {
        try {
          const result = await fetch(url, {
            method: 'GET',
            headers: {
              ...headers,
              'access-token': getToken() as string,
              uid: getUid() as string,
              client: getClient() as string
            }
          })
            .then(res => res.json())
            .then(res => {
              const link = document.createElement('a')
              link.setAttribute('target', '_blank')
              link.href = res.url
              link.click()
              link.parentNode?.removeChild(link)
              return res
            })
            .finally(() => setUrl(undefined))
          setData(result)
        } catch (err) {
          setError(err)
        }
      }
      setLoading(false)
    }

    if (url) fetchData()
  }, [url, headers])
  return [{ data, loading, error }, setUrl]
}

export const useLocalStorageJSON = (key: string, initialState?: any) => {
  const serializedInitialState = JSON.stringify(initialState)
  let storageValue = initialState
  try {
    const item = localStorage.getItem(key)
    storageValue = typeof item === 'string' ? JSON.parse(item) : initialState
  } catch {
    localStorage.setItem(key, serializedInitialState)
  }
  const [value, setValue] = useState(storageValue)
  const updatedSetValue = useCallback(
    (newValue: any) => {
      const serializedNewValue = JSON.stringify(newValue)
      if (serializedNewValue === serializedInitialState || typeof newValue === 'undefined') {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, serializedNewValue)
      }
      setValue(newValue ?? initialState)
    },
    [initialState, serializedInitialState, key]
  )
  return [value, updatedSetValue]
}

export const useLocalStorage = (key: string, initialState?: any) => {
  const [value, setValue] = useState(localStorage.getItem(key) ?? initialState)
  const updatedSetValue = useCallback(
    (newValue: any) => {
      if (newValue === initialState || typeof newValue === 'undefined') {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, newValue)
      }
      setValue(newValue ?? initialState)
    },
    [initialState, key]
  )
  return [value, updatedSetValue]
}

export const useLocationChange = (action: (location: any) => {}) => {
  const location = useLocation()
  useEffect(() => {
    action(location)
  }, [action, location])
}

export const useCase = () => {
  const caseContextValues = useContext(CaseContext)
  return caseContextValues
}

export const useTitle = () => {
  const titleContextValues = useContext(TitleContext)
  return titleContextValues
}

export const useProfile = () => {
  const profileContextValues = useContext(ProfileContext)
  return profileContextValues
}

export const useUser = () => {
  const userContextValues = useContext(UserContext)
  return userContextValues
}

export const useNotification = (config?: IMessageBoxConfig) => {
  const notificationContextValues = useContext(NotificationContext)
  if (!notificationContextValues) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  const { notification, setNotification } = notificationContextValues
  const handleNotification = useCallback(
    (newNotification: INotification) => {
      setNotification(oldNotification => ({
        ...oldNotification,
        ...config,
        ...newNotification
      }))
    },
    [config, setNotification]
  )
  return { notification, setNotification: handleNotification }
}

export { default as useBeforeUnloadWarning } from './hooks/useBeforeUnloadWarning'
export { default as useUnsavedChangesDialog } from './hooks/useUnsavedChangesDialog'
// M7: gleiches Re-Export-Muster wie oben für die AI-Chat-Hooks. Beide
// importieren ihrerseits aus '../hooks' (useUser/useCase/useTitle) und
// schließen damit einen Zirkelbezug mit dieser Datei — für ESM/Vite
// unproblematisch, da die Hooks erst zur Render-Zeit aufgerufen werden,
// nicht beim Modul-Load (siehe tsc/build-Verifikation im Report).
export { default as useAiChatChannel } from './hooks/useAiChatChannel'
export { useCurrentViewContext } from './hooks/useCurrentViewContext'

export const useSafeRefetch = (refetch: () => Promise<any>) => {
  const isRefetchingRef = useRef(false)
  const [isRefetching, setIsRefetching] = useState(false)

  const handleRefetch = useCallback(async () => {
    if (isRefetchingRef.current) return
    isRefetchingRef.current = true
    setIsRefetching(true)
    try {
      await refetch()
    } catch (err) {
      console.error(err)
    } finally {
      isRefetchingRef.current = false
      setIsRefetching(false)
    }
  }, [refetch])

  return { isRefetching, handleRefetch }
}
